import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { cartAPI, settingsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const CartContext = createContext(null);

// Safe localStorage — Safari Private Browsing and locked-down in-app webviews
// (Instagram/Facebook browsers, some Android WebViews) throw on access/write.
// Wrapping prevents an exception from breaking add-to-cart on those platforms.
const safeLS = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* storage unavailable */ } },
  remove: (k) => { try { localStorage.removeItem(k); } catch { /* storage unavailable */ } },
};

export const CartProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const isAuthed = !!user;
  const [items, setItems] = useState(() => {
    try { return JSON.parse(safeLS.get('cart') || '[]'); } catch { return []; }
  });
  const [loaded, setLoaded] = useState(false);
  const [multiSupplierAllowed, setMultiSupplierAllowed] = useState(
    () => safeLS.get('multi_supplier_allowed') === 'true'
  );
  // Pending add when the user has an unresolved supplier conflict.
  const [pendingReplace, setPendingReplace] = useState(null); // { product, quantity, currentSupplierName, incomingSupplierName }
  const saveTimer = useRef(null);

  // Public settings (independent of auth).
  useEffect(() => {
    let active = true;
    settingsAPI.getPublic()
      .then(r => {
        if (!active) return;
        const allowed = !!r.data.allow_multi_supplier_carts;
        setMultiSupplierAllowed(allowed);
        // Cache so a transient fetch failure / rate-limit doesn't flip the flag
        // to false and wrongly block a legitimate multi-supplier cart.
        safeLS.set('multi_supplier_allowed', allowed ? 'true' : 'false');
      })
      .catch(() => { /* keep last-known cached value */ });
    return () => { active = false; };
  }, []);

  // Load the server-side cart once auth state is resolved. Anonymous users keep
  // their localStorage cart.
  useEffect(() => {
    if (authLoading) return undefined;
    let active = true;
    if (isAuthed) {
      cartAPI.get()
        .then(r => {
          if (!active) return;
          if (r.data.items?.length > 0) setItems(r.data.items);
          setLoaded(true);
        })
        .catch(() => { if (active) setLoaded(true); });
    } else {
      setLoaded(true);
    }
    return () => { active = false; };
  }, [authLoading, isAuthed]);

  // Debounced save to server
  const saveToServer = useCallback((newItems) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (isAuthed) {
        cartAPI.save(newItems).catch(() => {});
      }
      safeLS.set('cart', JSON.stringify(newItems));
    }, 500);
  }, [isAuthed]);

  // Internal: actually add the item without supplier-conflict checks.
  const performAdd = useCallback((product, quantity) => {
    setItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      let updated;
      if (existing) {
        updated = prev.map(i =>
          i.product_id === product.id
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      } else {
        const moq = Number(product.moq || 0);
        updated = [...prev, {
          product_id: product.id,
          name: product.name,
          price: product.price,
          quantity: moq > 1 ? Math.max(quantity, moq) : quantity,
          image_url: product.image_url || '',
          supplier_id: product.supplier_id || '',
          supplier_name: product.supplier_name || '',
          category_name: product.category_name || '',
          unit_size_value: product.unit_size_value || '',
          unit_size_unit: product.unit_size_unit || '',
          pack_qty: Number(product.pack_qty) || 1,
          moq,
        }];
      }
      saveToServer(updated);
      return updated;
    });
  }, [saveToServer]);

  const addItem = useCallback((product, quantity = 1) => {
    // Single-supplier-cart guard.
    if (!multiSupplierAllowed && items.length > 0) {
      const cartSupplierId = items[0].supplier_id || '';
      const incomingSupplierId = product.supplier_id || '';
      if (cartSupplierId && incomingSupplierId && cartSupplierId !== incomingSupplierId) {
        setPendingReplace({
          product,
          quantity,
          currentSupplierName: items[0].supplier_name || 'another supplier',
          incomingSupplierName: product.supplier_name || 'this supplier',
        });
        return { conflict: true };
      }
    }
    performAdd(product, quantity);
    return { conflict: false };
  }, [items, multiSupplierAllowed, performAdd]);

  // Confirm: clear the cart and add the pending item.
  const confirmReplace = useCallback(() => {
    if (!pendingReplace) return;
    const { product, quantity } = pendingReplace;
    setItems([]);
    safeLS.remove('cart');
    if (isAuthed) cartAPI.save([]).catch(() => {});
    // Defer the add so React commits the cleared state first.
    setTimeout(() => performAdd(product, quantity), 0);
    setPendingReplace(null);
  }, [pendingReplace, performAdd, isAuthed]);

  const cancelReplace = useCallback(() => setPendingReplace(null), []);

  const removeItem = useCallback((productId) => {
    setItems(prev => {
      const updated = prev.filter(i => i.product_id !== productId);
      saveToServer(updated);
      return updated;
    });
  }, [saveToServer]);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) return removeItem(productId);
    setItems(prev => {
      const updated = prev.map(i =>
        i.product_id === productId ? { ...i, quantity } : i
      );
      saveToServer(updated);
      return updated;
    });
  }, [saveToServer, removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    safeLS.remove('cart');
    if (isAuthed) cartAPI.save([]).catch(() => {});
  }, [isAuthed]);

  // Keep only the items belonging to one supplier (used to resolve a
  // multi-supplier cart when the store only allows single-supplier orders).
  const keepOnlySupplier = useCallback((supplierId) => {
    setItems(prev => {
      const updated = prev.filter(i => (i.supplier_id || '') === (supplierId || ''));
      saveToServer(updated);
      return updated;
    });
  }, [saveToServer]);

  const total = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);
  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const currentSupplierId = items[0]?.supplier_id || '';
  const currentSupplierName = items[0]?.supplier_name || '';

  const value = useMemo(() => ({
    items, addItem, removeItem, updateQuantity, clearCart, keepOnlySupplier, total, itemCount, loaded,
    pendingReplace, confirmReplace, cancelReplace,
    multiSupplierAllowed, currentSupplierId, currentSupplierName,
  }), [
    items, addItem, removeItem, updateQuantity, clearCart, keepOnlySupplier, total, itemCount, loaded,
    pendingReplace, confirmReplace, cancelReplace,
    multiSupplierAllowed, currentSupplierId, currentSupplierName,
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
